/**
 * Core pitch model.
 *
 * Everything in MusicMentor is spelled, not just sounded: `C#4` and `Db4` are the
 * same key on a piano but different musical objects, and harmony class cares about
 * the difference. So a Pitch always carries a letter, an alteration, and an octave.
 */

export const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;
export type Letter = (typeof LETTERS)[number];

/** Semitone offset of each natural letter above C. */
const NATURAL_SEMITONES: Record<Letter, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

/** Semitones above the tonic for each diatonic step of a major scale, extended past the octave. */
export const MAJOR_SCALE_SEMITONES = [0, 2, 4, 5, 7, 9, 11, 12, 14, 16, 17, 19, 21, 23, 24];

export interface Pitch {
  letter: Letter;
  /** -2 = double flat, -1 = flat, 0 = natural, 1 = sharp, 2 = double sharp. */
  alter: number;
  /** Scientific pitch notation: middle C is C4. */
  octave: number;
}

export const ACCIDENTAL_GLYPHS: Record<number, string> = {
  [-2]: '𝄫',
  [-1]: '♭',
  0: '',
  1: '♯',
  2: '𝄪',
};

/** ASCII accidental suffix, which is what VexFlow and most text input expect. */
export const ACCIDENTAL_ASCII: Record<number, string> = {
  [-2]: 'bb',
  [-1]: 'b',
  0: '',
  1: '#',
  2: '##',
};

export function letterIndex(letter: Letter): number {
  return LETTERS.indexOf(letter);
}

/**
 * Absolute diatonic step: counts letter names only, ignoring accidentals.
 * C4 = 28, D4 = 29 ... B4 = 34, C5 = 35. One step is one line-or-space on a staff.
 */
export function diatonicStep(pitch: Pitch): number {
  return pitch.octave * 7 + letterIndex(pitch.letter);
}

export function pitchFromDiatonicStep(step: number, alter = 0): Pitch {
  const octave = Math.floor(step / 7);
  const index = ((step % 7) + 7) % 7;
  return { letter: LETTERS[index], alter, octave };
}

/** MIDI note number. Middle C (C4) = 60. */
export function midi(pitch: Pitch): number {
  return (pitch.octave + 1) * 12 + NATURAL_SEMITONES[pitch.letter] + pitch.alter;
}

/** 0-11, where 0 = C. */
export function pitchClass(pitch: Pitch): number {
  return ((midi(pitch) % 12) + 12) % 12;
}

export function naturalPitchClass(letter: Letter): number {
  return NATURAL_SEMITONES[letter];
}

export function pitchName(pitch: Pitch): string {
  return `${pitch.letter}${ACCIDENTAL_GLYPHS[pitch.alter] ?? ''}`;
}

export function pitchNameWithOctave(pitch: Pitch): string {
  return `${pitchName(pitch)}${pitch.octave}`;
}

/** e.g. `c#/4` — the key format VexFlow's StaveNote wants. */
export function toVexKey(pitch: Pitch): string {
  return `${pitch.letter.toLowerCase()}${ACCIDENTAL_ASCII[pitch.alter] ?? ''}/${pitch.octave}`;
}

export function frequency(pitch: Pitch): number {
  return 440 * Math.pow(2, (midi(pitch) - 69) / 12);
}

export function samePitch(a: Pitch, b: Pitch): boolean {
  return a.letter === b.letter && a.alter === b.alter && a.octave === b.octave;
}

/**
 * Parse things a learner might type: "C", "Bb", "F#3", "Ebb5", "c#/4".
 * Returns null rather than guessing when the text isn't a pitch.
 */
export function parsePitch(text: string, defaultOctave = 4): Pitch | null {
  const cleaned = text.trim().replace('/', '').replace(/♭/g, 'b').replace(/♯/g, '#');
  const match = /^([A-Ga-g])(bb|b|##|#|natural|n|)(-?\d+)?$/.exec(cleaned);
  if (!match) return null;
  const letter = match[1].toUpperCase() as Letter;
  const alterText = match[2].toLowerCase();
  const alter =
    alterText === 'bb' ? -2 : alterText === 'b' ? -1 : alterText === '#' ? 1 : alterText === '##' ? 2 : 0;
  const octave = match[3] !== undefined ? Number(match[3]) : defaultOctave;
  return { letter, alter, octave };
}

/** True for spellings that exist on paper but are unusual in practice (E#, Cb, double accidentals). */
export function isAwkwardSpelling(pitch: Pitch): boolean {
  if (Math.abs(pitch.alter) >= 2) return true;
  if (pitch.alter === 1 && (pitch.letter === 'E' || pitch.letter === 'B')) return true;
  if (pitch.alter === -1 && (pitch.letter === 'C' || pitch.letter === 'F')) return true;
  return false;
}

/**
 * Move a pitch by an interval — a number of letter steps *and* a number of
 * semitones — and spell the result correctly.
 *
 * This is the primitive that keeps enharmonic spelling intact, and it is the
 * reason it lives here rather than being rewritten wherever a note needs
 * moving. Transposing by semitones alone and then naming the result from a
 * twelve-entry table throws the letter away: three semitones above B♭ comes back
 * as C♯ instead of D♭, which turns a minor third into an augmented second. The
 * letter is chosen first, from the diatonic distance; the accidental is then
 * whatever makes the sounding pitch come out right, including a double
 * accidental when the interval genuinely calls for one.
 */
export function transposeBy(pitch: Pitch, diatonicSteps: number, semitones: number): Pitch {
  const step = diatonicStep(pitch) + diatonicSteps;
  const octave = Math.floor(step / 7);
  const letter = LETTERS[((step % 7) + 7) % 7];
  const naturalMidi = (octave + 1) * 12 + NATURAL_SEMITONES[letter];
  return { letter, alter: midi(pitch) + semitones - naturalMidi, octave };
}

/** Interval between two pitches, named the way a theory class names it (e.g. "M3", "P5", "m7"). */
export function intervalName(low: Pitch, high: Pitch): string {
  const steps = diatonicStep(high) - diatonicStep(low);
  const semitones = midi(high) - midi(low);
  const number = Math.abs(steps) + 1;
  const simpleSteps = ((Math.abs(steps) % 7) + 7) % 7;
  const simpleSemitones = Math.abs(semitones) - Math.floor(Math.abs(steps) / 7) * 12;
  const majorReference = MAJOR_SCALE_SEMITONES[simpleSteps];
  const diff = simpleSemitones - majorReference;
  const perfectDegrees = [0, 3, 4];
  const isPerfect = perfectDegrees.includes(simpleSteps);

  /**
   * The quality ladder, stated as a rule rather than a lookup.
   *
   * A table that stops at "doubly" has to fall back to something when it runs
   * out, and the fallback used to be the raw difference — so D up to C𝄫, a
   * seventh two double-flats wide, came out as "-37" and went straight to the
   * learner through the interval readout in BuildLearn. The ladder is
   * unbounded in both directions:
   *
   *   perfect degrees      … ddd  dd  d  P  A  AA  AAA …
   *   major/minor degrees  … ddd  dd  d  m  M  A   AA  …
   *
   * so the only difference between them is that an imperfect degree spends one
   * rung on `m` before it starts diminishing.
   */
  let quality: string;
  if (diff === 0) quality = isPerfect ? 'P' : 'M';
  else if (diff > 0) quality = 'A'.repeat(diff);
  else if (isPerfect) quality = 'd'.repeat(-diff);
  else quality = diff === -1 ? 'm' : 'd'.repeat(-diff - 1);

  return `${quality}${number}`;
}
